import { useCallback } from 'react';

import { useMemberOperatorTaskContext } from 'modules/common/components/MemberOperator';
import { TaskStatus } from 'modules/common/components/MemberOperator/MemberOperator.interfaces';
import { SubmitActionTask } from 'modules/Tasks/types';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

interface CompleteMemberTaskOptions {
    /** Segment event fired once the status update lands. */
    trackingEvent: string;
    /** Shared by the tracking event and the Datadog error context. */
    trackingProperties?: Record<string, unknown>;
    completionPayload?: Record<string, unknown>;
    /**
     * Runs last, once the task is completed and every member-task surface has finished refreshing.
     * Awaited after the refetches specifically so a caller that starts a file download here cannot
     * abort them — WebKit cancels in-flight requests when a blob save begins.
     */
    onSuccess?: () => void;
    /** Runs after the failure is logged, for whatever error state the modal shows. */
    onError?: (error: Error) => void;
}

/**
 * The shared tail of every submit-action modal: mark the task completed, announce it, refresh every
 * member-task surface, then close. Each modal owns how it *earns* completion — an upload committing,
 * a file downloading — but the completion itself is identical, so it lives here rather than being
 * re-implemented per modal where the semantics drift apart.
 */
export default function useCompleteMemberTask(task: SubmitActionTask, onClose: () => void) {
    const { updateTaskStatus, sendTrackingEvent } = useMemberOperatorTaskContext();
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();

    return useCallback(
        ({ trackingEvent, trackingProperties, completionPayload, onSuccess, onError }: CompleteMemberTaskOptions) => {
            updateTaskStatus(
                {
                    status: TaskStatus.COMPLETED,
                    execute_handlers_async: true,
                    ...(completionPayload ? { completion_payload: completionPayload } : {}),
                },
                {
                    onSuccess: async () => {
                        sendTrackingEvent(trackingEvent, trackingProperties);
                        // Awaited, not fire-and-forget: onSuccess below may start a file download,
                        // and on WebKit that aborts every request still in flight — which would
                        // leave every member-task surface showing the task it just completed.
                        await invalidateMemberTaskQueries(task);
                        onClose();
                        onSuccess?.();
                    },
                    onError: (error) => {
                        sendDatadogRumError('Task status update failed', error, {
                            taskId: task.id,
                            ...trackingProperties,
                        });
                        onError?.(error);
                    },
                }
            );
        },
        [updateTaskStatus, sendTrackingEvent, invalidateMemberTaskQueries, task, onClose]
    );
}
